import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { Translate } from '~/shared/internationalization/translation';
import type { RealtimeEventListenerBuilder } from '~/shared/realtime-event-listener/realtime-event-listener';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { ChatFetchers } from './chat/chat-fetchers';
import { createChatFetchers } from './chat/chat-fetchers';
import type { CompanyProfileFetchers } from './company-profile/company-profile-fetchers';
import { createCompanyProfileFetchers } from './company-profile/company-profile-fetchers';
import type { CompanySettingsFetchers } from './company-settings/company-settings-fetchers';
import { createCompanySettingsFetchers } from './company-settings/company-settings-fetchers';
import type { DocumentsFetchers } from './documents/documents-fetchers';
import { createDocumentsFetchers } from './documents/documents-fetchers';
import type { InternalTrackingFetchers } from './internal-tracking/internal-tracking-fetchers';
import { createInternalTrackingFetchers } from './internal-tracking/internal-tracking-fetchers';
import type { JobArchitectureFetchers } from './job-architecture/job-architecture-fetchers';
import { createJobArchitectureFetchers } from './job-architecture/job-architecture-fetchers';
import { createSkillFetchers, type SkillFetchers } from './skill/skill-fetchers';
import type { UserManagementFetchers } from './user-management/user-management-fetchers';
import { createUserManagementFetchers } from './user-management/user-management-fetchers';

export type ApplicationFetchers = ChatFetchers &
    CompanyProfileFetchers &
    CompanySettingsFetchers &
    DocumentsFetchers &
    InternalTrackingFetchers &
    JobArchitectureFetchers &
    SkillFetchers &
    UserManagementFetchers;

export type { AssignableReviewersItem, AssignReviewerItem } from './job-architecture/job-architecture-fetchers';

export type ApplicationFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createApplicationFetchers = (dependencies: ApplicationFetchersDependencies): ApplicationFetchers => {
    return {
        ...createChatFetchers(dependencies),
        ...createCompanyProfileFetchers(dependencies),
        ...createCompanySettingsFetchers(dependencies),
        ...createDocumentsFetchers(dependencies),
        ...createJobArchitectureFetchers(dependencies),
        ...createSkillFetchers(dependencies),
        ...createInternalTrackingFetchers(dependencies),
        ...createUserManagementFetchers(dependencies),
    };
};
